import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  IconButton,
  Divider,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import apiClient from '../utils/apiInterceptor';
import { motion, AnimatePresence } from 'framer-motion';

const Feedback = () => {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'other'
  });

  const showMessage = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/feedback/my', {
        params: {
          page,
          limit: 10
        }
      });

      if (response.data.success) {
        setFeedbacks(response.data.feedbacks || []);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (error) {
      console.error('获取反馈列表失败:', error);
      showMessage('获取反馈列表失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [page]);

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      showMessage('请填写标题和内容', 'error');
      return;
    }

    try {
      const response = await apiClient.post('/feedback', formData);
      if (response.data.success) {
        showMessage('反馈提交成功', 'success');
        setOpenDialog(false);
        setFormData({ title: '', content: '', type: 'other' });
        fetchFeedbacks();
      }
    } catch (error) {
      console.error('提交反馈失败:', error);
      showMessage(error.response?.data?.message || '提交反馈失败', 'error');
    }
  };

  const handleViewDetail = async (id) => {
    try {
      const response = await apiClient.get(`/feedback/${id}`);
      if (response.data.success) {
        setSelectedFeedback(response.data.feedback);
        await apiClient.put(`/feedback/${id}/read`);
        fetchFeedbacks();
      }
    } catch (error) {
      console.error('获取反馈详情失败:', error);
      showMessage('获取反馈详情失败', 'error');
    }
  };

  const handleUserReply = async () => {
    if (!replyContent.trim()) {
      showMessage('请输入回复内容', 'error');
      return;
    }

    if (!selectedFeedback) return;

    if (selectedFeedback.status === 'closed') {
      showMessage('已关闭的反馈不能回复', 'error');
      return;
    }

    try {
      const response = await apiClient.post(
        `/feedback/${selectedFeedback._id}/reply`,
        { content: replyContent.trim() }
      );
      
      if (response.data.success) {
        showMessage('回复成功', 'success');
        setSelectedFeedback(response.data.feedback);
        setReplyContent('');
        fetchFeedbacks();
      }
    } catch (error) {
      console.error('回复失败:', error);
      showMessage(error.response?.data?.message || '回复失败', 'error');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      processing: 'info',
      resolved: 'success',
      closed: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: '待处理',
      processing: '处理中',
      resolved: '已解决',
      closed: '已关闭'
    };
    return texts[status] || status;
  };

  const getTypeIcon = (type) => {
    const icons = {
      bug: <BugReportIcon sx={{ fontSize: 16 }} />,
      feature: <LightbulbIcon sx={{ fontSize: 16 }} />,
      suggestion: <FeedbackIcon sx={{ fontSize: 16 }} />,
      complaint: <ReportProblemIcon sx={{ fontSize: 16 }} />,
      other: <HelpOutlineIcon sx={{ fontSize: 16 }} />
    };
    return icons[type] || <HelpOutlineIcon sx={{ fontSize: 16 }} />;
  };

  const getTypeText = (type) => {
    const texts = {
      bug: '错误报告',
      feature: '功能建议',
      suggestion: '改进建议',
      complaint: '投诉',
      other: '其他'
    };
    return texts[type] || type;
  };

  const filteredFeedbacks = filter === 'all' 
    ? feedbacks 
    : feedbacks.filter(f => f.status === filter);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* 顶部导航栏 - Cyber风格 */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            px: 2,
            py: 1.5,
            borderRadius: '10px',
            background: 'rgba(8, 14, 24, 0.9)',
            border: '1px solid rgba(34, 211, 238, 0.2)',
            boxShadow: '0 0 20px rgba(34, 211, 238, 0.1)',
          }}>
            <Button
              variant="text"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              sx={{
                color: 'rgba(224, 242, 254, 0.7)',
                '&:hover': { color: '#22d3ee', bgcolor: 'rgba(34, 211, 238, 0.1)' },
                fontSize: '0.8rem',
              }}
            >
              返回
            </Button>

            <Typography className="workspace-panel-title">
              我的反馈
            </Typography>

            <Button
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setOpenDialog(true)}
              className="workspace-btn-neon-cyan"
            >
              提交反馈
            </Button>
          </Box>
        </motion.div>

        {/* 主内容区 */}
        <Paper className="cyber-card" sx={{ p: 0, overflow: 'hidden' }}>
          {/* 筛选器 */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(34, 211, 238, 0.1)' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                sx={{
                  color: 'rgba(224, 242, 254, 0.8)',
                  fontSize: '0.8rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(34, 211, 238, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(34, 211, 238, 0.5)',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(224, 242, 254, 0.6)',
                  },
                }}
              >
                <MenuItem value="all">全部</MenuItem>
                <MenuItem value="pending">待处理</MenuItem>
                <MenuItem value="processing">处理中</MenuItem>
                <MenuItem value="resolved">已解决</MenuItem>
                <MenuItem value="closed">已关闭</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ p: 1.5 }}>
            {loading ? (
              <Box textAlign="center" py={4}>
                <CircularProgress size={24} sx={{ color: '#22d3ee' }} />
              </Box>
            ) : filteredFeedbacks.length === 0 ? (
              <Box textAlign="center" py={4}>
                <FeedbackIcon sx={{ fontSize: 36, color: 'rgba(224, 242, 254, 0.3)', mb: 1 }} />
                <Typography sx={{ color: 'rgba(224, 242, 254, 0.5)', fontSize: '0.85rem' }}>
                  暂无反馈记录
                </Typography>
              </Box>
            ) : (
              <>
                {filteredFeedbacks.map((feedback, index) => (
                  <motion.div
                    key={feedback._id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card
                      className="cyber-card"
                      onClick={() => handleViewDetail(feedback._id)}
                      sx={{
                        cursor: 'pointer',
                        mb: 1,
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'rgba(34, 211, 238, 0.4)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                          <Box sx={{ color: 'rgba(224, 242, 254, 0.6)' }}>
                            {getTypeIcon(feedback.type)}
                          </Box>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(224, 242, 254, 0.95)', flex: 1 }}>
                            {feedback.title}
                          </Typography>
                          <Chip
                            label={getStatusText(feedback.status)}
                            size="small"
                            color={getStatusColor(feedback.status)}
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(224, 242, 254, 0.45)', lineHeight: 1.5 }}>
                          {feedback.content?.slice(0, 80)}...
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}

                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(e, p) => setPage(p)}
                      size="small"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          color: 'rgba(224, 242, 254, 0.6)',
                        },
                        '& .MuiPaginationItem-root.Mui-selected': {
                          bgcolor: 'rgba(34, 211, 238, 0.15)',
                          color: '#22d3ee',
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Paper>

        {/* 提交反馈对话框 */}
        <Dialog
          open={openDialog}
          onClose={() => {
            setOpenDialog(false);
            setFormData({ title: '', content: '', type: 'other' });
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgba(8, 14, 24, 0.98)',
              borderRadius: '12px',
              border: '1px solid rgba(34, 211, 238, 0.2)',
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid rgba(34, 211, 238, 0.15)', pb: 1.5 }}>
            <Typography className="workspace-panel-title" sx={{ fontSize: '0.95rem !important' }}>
              提交反馈
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ py: 2 }}>
            <Box className="cyber-input" sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="标题"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                InputLabelProps={{ sx: { color: 'rgba(224, 242, 254, 0.6)', fontSize: '0.85rem' } }}
                InputProps={{ sx: { color: '#fff', fontSize: '0.85rem' } }}
              />
            </Box>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel sx={{ color: 'rgba(224, 242, 254, 0.6)', fontSize: '0.85rem' }}>反馈类型</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                label="反馈类型"
                sx={{
                  color: '#fff',
                  fontSize: '0.85rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(34, 211, 238, 0.3)',
                  },
                }}
              >
                <MenuItem value="bug">错误报告</MenuItem>
                <MenuItem value="feature">功能建议</MenuItem>
                <MenuItem value="suggestion">改进建议</MenuItem>
                <MenuItem value="complaint">投诉</MenuItem>
                <MenuItem value="other">其他</MenuItem>
              </Select>
            </FormControl>
            <Box className="cyber-input">
              <TextField
                fullWidth
                multiline
                rows={4}
                label="详细内容"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                InputLabelProps={{ sx: { color: 'rgba(224, 242, 254, 0.6)', fontSize: '0.85rem' } }}
                InputProps={{ sx: { color: '#fff', fontSize: '0.85rem' } }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2, borderTop: '1px solid rgba(34, 211, 238, 0.1)' }}>
            <Button
              onClick={() => {
                setOpenDialog(false);
                setFormData({ title: '', content: '', type: 'other' });
              }}
              sx={{ color: 'rgba(224, 242, 254, 0.5)' }}
            >
              取消
            </Button>
            <Button
              onClick={handleSubmit}
              variant="outlined"
              className="workspace-btn-neon-cyan"
              startIcon={<SendIcon sx={{ fontSize: 16 }} />}
            >
              提交
            </Button>
          </DialogActions>
        </Dialog>

        {/* 反馈详情对话框 */}
        <Dialog
          open={!!selectedFeedback}
          onClose={() => {
            setSelectedFeedback(null);
            setReplyContent('');
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgba(8, 14, 24, 0.98)',
              borderRadius: '12px',
              border: '1px solid rgba(34, 211, 238, 0.2)',
            }
          }}
        >
          {selectedFeedback && (
            <>
              <DialogTitle sx={{ borderBottom: '1px solid rgba(34, 211, 238, 0.15)', pb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography className="workspace-panel-title" sx={{ fontSize: '0.9rem !important' }}>
                    {selectedFeedback.title}
                  </Typography>
                  <IconButton
                    onClick={() => {
                      setSelectedFeedback(null);
                      setReplyContent('');
                    }}
                    size="small"
                    sx={{ color: 'rgba(224, 242, 254, 0.5)' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <Chip
                    icon={getTypeIcon(selectedFeedback.type)}
                    label={getTypeText(selectedFeedback.type)}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      bgcolor: 'rgba(34, 211, 238, 0.1)',
                      color: '#22d3ee',
                      border: '1px solid rgba(34, 211, 238, 0.2)',
                    }}
                  />
                  <Chip
                    label={getStatusText(selectedFeedback.status)}
                    size="small"
                    color={getStatusColor(selectedFeedback.status)}
                    sx={{ height: 24, fontSize: '0.75rem' }}
                  />
                </Box>
                <Typography sx={{ color: '#fff', mb: 2, fontSize: '0.85rem', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {selectedFeedback.content}
                </Typography>
                
                <Divider sx={{ borderColor: 'rgba(34, 211, 238, 0.1)', mb: 2 }} />
                
                {selectedFeedback.replies && selectedFeedback.replies.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ color: '#22d3ee', fontSize: '0.8rem', fontWeight: 600, mb: 1 }}>
                      回复 ({selectedFeedback.replies.length})
                    </Typography>
                    {selectedFeedback.replies.map((reply, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 1.25,
                          mb: 1,
                          borderRadius: '8px',
                          bgcolor: reply.isAdmin ? 'rgba(34, 211, 238, 0.05)' : 'rgba(8, 14, 24, 0.5)',
                          border: '1px solid rgba(34, 211, 238, 0.1)',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: reply.isAdmin ? '#22d3ee' : 'rgba(224, 242, 254, 0.8)' }}>
                            {reply.isAdmin ? '管理员' : reply.repliedByUsername}
                          </Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: 'rgba(224, 242, 254, 0.4)' }}>
                            {new Date(reply.repliedAt).toLocaleString('zh-CN')}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.8rem', color: '#fff', whiteSpace: 'pre-wrap' }}>
                          {reply.content}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {selectedFeedback.status !== 'closed' && (
                  <>
                    <Divider sx={{ borderColor: 'rgba(34, 211, 238, 0.1)', mb: 1.5 }} />
                    <Typography sx={{ color: '#22d3ee', fontSize: '0.75rem', fontWeight: 600, mb: 1 }}>
                      添加回复
                    </Typography>
                    <Box className="cyber-input">
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="输入您的回复..."
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        InputProps={{ sx: { color: '#fff', fontSize: '0.85rem' } }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                      <Button
                        variant="outlined"
                        onClick={handleUserReply}
                        disabled={!replyContent.trim()}
                        className="workspace-btn-neon-cyan"
                        startIcon={<SendIcon sx={{ fontSize: 16 }} />}
                      >
                        发送回复
                      </Button>
                    </Box>
                  </>
                )}
              </DialogContent>
            </>
          )}
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </motion.div>
  );
};

export default Feedback;
